import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_DASHBOARD_SUMMARY } from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { apiJson } from '../utils/apiClient';

const quickLinks = [
  { to: '/centers', label: 'Manage Centers', helper: 'Create and update branch centers.', moduleKey: 'centers' },
  { to: '/clients', label: 'Manage Clients', helper: 'Review active and inactive client records.', moduleKey: 'clients' },
  { to: '/loan-disbursement', label: 'New Disbursement', helper: 'Register a fresh loan disbursement.', moduleKey: 'disbursement' },
  { to: '/loan-repayment-list', label: 'Bulk Repayment', helper: 'Process center repayments faster.', moduleKey: 'repayments' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Dashboard = () => {
  const { canAccess, user, selectedBranchId } = useAuth();
  const isEmployeeDashboard = user?.role_key === 'employee';
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [stats, setStats] = useState({
    totalCenters: 0,
    totalClients: 0,
    activeClients: 0,
    inactiveClients: 0,
    activeAdvanceCases: 0,
    projectedCollection: 0,
    averageFutureCollection: 0,
  });
  const [topCenters, setTopCenters] = useState([]);
  const [upcomingRepayments, setUpcomingRepayments] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      if (isEmployeeDashboard) {
        setErrorMessage('');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage('');

        const { response, data } = await apiJson(API_DASHBOARD_SUMMARY, { method: 'POST', body: JSON.stringify({}) });
        if (!response.ok || !data?.status) throw new Error(data?.message || 'Unable to load dashboard summary.');
        const summary = data.data || {};

        setStats({
          totalCenters: summary.total_centers || 0,
          totalClients: summary.total_clients || 0,
          activeClients: summary.active_clients || 0,
          inactiveClients: summary.inactive_clients || 0,
          activeAdvanceCases: summary.active_advance_cases || 0,
          projectedCollection: summary.projected_collection || 0,
          averageFutureCollection: summary.average_future_collection || 0,
        });
        setTopCenters(summary.top_centers || []);
        setUpcomingRepayments(summary.upcoming_repayments || []);
        setWeeklySummary(summary.weekly_summary || null);
      } catch (error) {
        setErrorMessage('Live dashboard data could not be loaded. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    void loadDashboard();
  }, [isEmployeeDashboard, selectedBranchId]);

  const visibleQuickLinks = quickLinks.filter((link) => canAccess(link.moduleKey, 'view'));

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">{isEmployeeDashboard ? 'Employee Workspace' : 'Operations Overview'}</p>
          <h1>{isEmployeeDashboard ? 'Choose an authorized task to begin.' : 'Track centers, clients, and collections from one place.'}</h1>
          <p className="dashboard-subtitle">
            {isEmployeeDashboard
              ? 'Only the operational tools assigned to your account are available here.'
              : 'This dashboard surfaces live portfolio activity so the branch team can quickly spot load, collection value, and the next work queue.'}
          </p>
        </div>
        {!isEmployeeDashboard ? (
          <div className="dashboard-highlight-card">
            <span className="dashboard-highlight-label">Future Collection Pipeline</span>
            <strong>Rs {isLoading ? '...' : formatCurrency(stats.projectedCollection)}</strong>
            <p>
              {isLoading
                ? 'Refreshing live figures.'
                : `${stats.activeAdvanceCases} active repayment case${
                    stats.activeAdvanceCases === 1 ? '' : 's'
                  } currently in queue.`}
            </p>
          </div>
        ) : null}
      </section>

      {errorMessage ? <div className="dashboard-banner-error">{errorMessage}</div> : null}

      {!isEmployeeDashboard ? <section className="dashboard-grid">
        <article className="dashboard-stat-card">
          <span>Total Centers</span>
          <strong>{isLoading ? '...' : stats.totalCenters}</strong>
          <p>Centers currently available for operations.</p>
        </article>
        <article className="dashboard-stat-card">
          <span>Total Clients</span>
          <strong>{isLoading ? '...' : stats.totalClients}</strong>
          <p>All client records currently stored in the system.</p>
        </article>
        <article className="dashboard-stat-card">
          <span>Active Clients</span>
          <strong>{isLoading ? '...' : stats.activeClients}</strong>
          <p>{isLoading ? 'Refreshing status split.' : `${stats.inactiveClients} inactive clients.`}</p>
        </article>
        <article className="dashboard-stat-card">
          <span>Average Future Collection</span>
          <strong>{isLoading ? '...' : `Rs ${formatCurrency(stats.averageFutureCollection)}`}</strong>
          <p>Average value still expected per advance case.</p>
        </article>
      </section> : null}

      {!isEmployeeDashboard && weeklySummary ? <section className="dashboard-panel mb-4">
        <div className="dashboard-section-header"><div><h2>Last 7 Days</h2><p>Branch operational activity from the previous seven days.</p></div></div>
        <div className="dashboard-grid">
          <article className="dashboard-stat-card"><span>Clients Added</span><strong>{weeklySummary.clients_added}</strong></article>
          <article className="dashboard-stat-card"><span>Centers Added</span><strong>{weeklySummary.centers_added}</strong></article>
          <article className="dashboard-stat-card"><span>Disbursement Clients</span><strong>{weeklySummary.disbursement_clients}</strong><p>Rs {formatCurrency(weeklySummary.disbursement_amount)} disbursed.</p></article>
          <article className="dashboard-stat-card"><span>Repayments</span><strong>Rs {formatCurrency(weeklySummary.repayment_amount)}</strong><p>Approved repayment entries.</p></article>
        </div>
      </section> : null}

      <section className={`dashboard-layout ${isEmployeeDashboard ? 'dashboard-layout-single' : ''}`}>
        <div className="dashboard-panel dashboard-panel-wide">
          <div className="dashboard-section-header">
            <div>
              <h2>Quick Actions</h2>
              <p>Jump straight into the most common field-office workflows.</p>
            </div>
          </div>
          <div className="dashboard-links">
            {visibleQuickLinks.map((link) => (
              <Link key={link.to} to={link.to} className="dashboard-link-card">
                <strong>{link.label}</strong>
                <span>{link.helper}</span>
              </Link>
            ))}
          </div>
        </div>

        {!isEmployeeDashboard ? <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <div>
              <h2>Portfolio Snapshot</h2>
              <p>Useful ratios for the current client base.</p>
            </div>
          </div>
          <div className="dashboard-metrics-list">
            <div className="dashboard-metric-row">
              <span>Active ratio</span>
              <strong>
                {isLoading || stats.totalClients === 0
                  ? '...'
                  : `${Math.round((stats.activeClients / stats.totalClients) * 100)}%`}
              </strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Inactive ratio</span>
              <strong>
                {isLoading || stats.totalClients === 0
                  ? '...'
                  : `${Math.round((stats.inactiveClients / stats.totalClients) * 100)}%`}
              </strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Centers with client records</span>
              <strong>{isLoading ? '...' : topCenters.length}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Advance case queue</span>
              <strong>{isLoading ? '...' : stats.activeAdvanceCases}</strong>
            </div>
          </div>
        </div> : null}
      </section>

      {!isEmployeeDashboard ? <section className="dashboard-layout">
        <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <div>
              <h2>Top Centers By Client Count</h2>
              <p>Largest center books based on current client records.</p>
            </div>
          </div>
          <div className="dashboard-table-wrap">
            <table className="dashboard-table data-table">
              <thead>
                <tr>
                  <th>Center</th>
                  <th>Total</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && topCenters.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="dashboard-table-empty">
                      No center data available yet.
                    </td>
                  </tr>
                ) : (
                  topCenters.map((center) => (
                    <tr key={center.centerNo}>
                      <td>
                        <strong>{center.centerName}</strong>
                        <span>{center.centerNo}</span>
                      </td>
                      <td>{center.totalClients}</td>
                      <td>{center.activeClients}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <div>
              <h2>Upcoming Repayment Queue</h2>
              <p>Preview the next clients with advance repayment exposure.</p>
            </div>
          </div>
          <div className="dashboard-queue">
            {!isLoading && upcomingRepayments.length === 0 ? (
              <div className="dashboard-empty-state">No upcoming advance repayment records found.</div>
            ) : (
              upcomingRepayments.map((item, index) => (
                <div key={`${item.client_no || 'client'}-${index}`} className="dashboard-queue-item">
                  <div>
                    <strong>{item.client_name || 'Unknown Client'}</strong>
                    <span>
                      {item.center_name || 'Unknown Center'} | {item.branch_name || 'Unknown Branch'}
                    </span>
                  </div>
                  <div className="dashboard-queue-meta">
                    <strong>Rs {formatCurrency(item.total_future_repayment_amt)}</strong>
                    <span>
                      {item.remaining_installments || 0} installment
                      {Number(item.remaining_installments || 0) === 1 ? '' : 's'} left
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section> : null}
    </div>
  );
};

export default Dashboard;
